import { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext'; // Add this import

// Global connection registry to prevent duplicate connections
const activeConnections = new Map<string, WebSocket>();
const connectionRefCounts = new Map<string, number>();

type ConnectionStatus = 'Connecting' | 'Open' | 'Closed' | 'Error';

interface UseWebSocketOptions {
  url: string;
  onMessage?: (message: MessageEvent) => void;
  onOpen?: (event: Event) => void;
  onClose?: (event: CloseEvent) => void;
  onError?: (event: Event) => void;
  reconnectAttempts?: number;
  reconnectInterval?: number;
  pingInterval?: number;
  disabled?: boolean; // Add a disabled option to prevent connections
  connectionId?: string; // Unique ID for this connection instance
  requireAuth?: boolean; // Add this option to require authentication
}

interface UseWebSocketReturn {
  sendMessage: (message: any) => boolean;
  sendCommand: (command: string, data?: any) => boolean;
  lastMessage: MessageEvent | null;
  connectionStatus: ConnectionStatus;
  reconnect: () => void;
}

export function useWebSocket({
  url,
  onMessage,
  onOpen,
  onClose,
  onError,
  reconnectAttempts = 5,
  reconnectInterval = 1000,
  pingInterval = 30000,
  disabled = false, // Default to enabled
  connectionId = `ws-${Date.now()}-${Math.random().toString(36).substring(2, 9)}`,
  requireAuth = true // Default to requiring authentication
}: UseWebSocketOptions): UseWebSocketReturn {
  const [lastMessage, setLastMessage] = useState<MessageEvent | null>(null);
  const [connectionStatus, setConnectionStatus] = useState<ConnectionStatus>('Connecting');
  const webSocketRef = useRef<WebSocket | null>(null);
  const reconnectTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const pingIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const reconnectCountRef = useRef<number>(0);
  const lastMessageTimeRef = useRef<number>(Date.now());
  const isDisabledRef = useRef<boolean>(disabled);
  const isComponentMountedRef = useRef<boolean>(true);
  const connectionUrlRef = useRef<string>('');
  
  // Get authentication state
  const { isAuthenticated } = useAuth();
  const isAuthenticatedRef = useRef<boolean>(isAuthenticated);

  // Update refs when props change
  useEffect(() => {
    isDisabledRef.current = disabled;
    isAuthenticatedRef.current = isAuthenticated;
    
    // If we're disabling and have an active connection, close it
    if ((disabled || (requireAuth && !isAuthenticated)) && webSocketRef.current) {
      const reason = disabled ? "Connection disabled by client" : "User not authenticated";
      console.log(`[WEBSOCKET] ${reason}, closing connection`);
      webSocketRef.current.close(1000, reason);
      
      // Clear any pending reconnect
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
        reconnectTimeoutRef.current = null;
      }
      
      // Clear ping interval
      if (pingIntervalRef.current) {
        clearInterval(pingIntervalRef.current);
        pingIntervalRef.current = null;
      }
      
      // Update status
      setConnectionStatus('Closed');
    }
  }, [disabled, isAuthenticated, requireAuth]);

  // Add a message queue to handle reconnection scenarios
  const messageQueueRef = useRef<any[]>([]);
  
  const sendMessage = useCallback((data: any): boolean => {
    const message = typeof data === 'string' ? data : JSON.stringify(data);
    
    if (webSocketRef.current && webSocketRef.current.readyState === WebSocket.OPEN) {
      try {
        webSocketRef.current.send(message);
        return true;
      } catch (error) {
        console.error('[WEBSOCKET] Error sending message:', error);
        // CRITICAL FIX: Update connection status when sending fails
        setConnectionStatus('Error');
        // Queue the message for retry
        messageQueueRef.current.push(message);
        return false;
      }
    } else {
      console.warn('[WEBSOCKET] Cannot send message, connection not open');
      // CRITICAL FIX: Update connection status to match reality
      if (connectionStatus === 'Open') {
        console.error('[WEBSOCKET] Connection status mismatch detected - fixing');
        setConnectionStatus('Closed');
        // Don't try to reconnect here - it causes a circular reference
        // Just update the status and let the component handle reconnection naturally
      }
      // Queue the message for when connection is established
      messageQueueRef.current.push(message);
      return false;
    }
  }, [connectionStatus]); // Removed reconnect from dependencies to fix circular reference

  const sendCommand = useCallback((command: string, data: any = {}): boolean => {
    return sendMessage({
      command,
      data
    });
  }, [sendMessage]);

  const connect = useCallback(async () => {
    // Don't connect if disabled, component unmounted, or not authenticated (when required)
    if (isDisabledRef.current || !isComponentMountedRef.current || 
        (requireAuth && !isAuthenticatedRef.current)) {
      let reason = "unknown reason";
      if (isDisabledRef.current) reason = "disabled flag is set";
      if (!isComponentMountedRef.current) reason = "component unmounted";
      if (requireAuth && !isAuthenticatedRef.current) reason = "user not authenticated";
      
      console.log(`[WEBSOCKET] Connection attempt prevented - ${reason}`);
      setConnectionStatus('Closed');
      return;
    }
    
    // Ensure URL is properly formatted for WebSocket connection
    let formattedUrl = url;
    if (!formattedUrl) {
      console.log('[WEBSOCKET] URL is null or empty, not connecting');
      setConnectionStatus('Closed');
      return;
    }
    
    if (!formattedUrl.startsWith('ws')) {
      // Convert http(s):// to ws(s)://
      formattedUrl = formattedUrl.replace(/^http/, 'ws');
    }

    // Make sure we're using the same host as the current page
    const currentHost = window.location.host;
    if (formattedUrl.includes('//192.168.1.29:')) {
      formattedUrl = formattedUrl.replace(/\/\/192\.168\.1\.29:[0-9]+/, `//${currentHost}`);
    }
    
    // Add connection ID as query parameter to help with debugging
    const urlObj = new URL(formattedUrl);
    urlObj.searchParams.set('client_id', connectionId);
    formattedUrl = urlObj.toString();
    
    // Store the formatted URL for reference
    connectionUrlRef.current = formattedUrl;
    
    // Clean up any existing connection
    if (webSocketRef.current) {
      if (webSocketRef.current.readyState !== WebSocket.CLOSED) {
        console.log('[WEBSOCKET] Closing existing connection before creating a new one');
        webSocketRef.current.close(1000, "Closing previous connection");
        // Wait a moment before creating a new connection
        await new Promise(resolve => setTimeout(resolve, 100));
      }
      webSocketRef.current = null;
    }

    // Clear any existing timeouts
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
      reconnectTimeoutRef.current = null;
    }

    if (pingIntervalRef.current) {
      clearInterval(pingIntervalRef.current);
      pingIntervalRef.current = null;
    }

    // Check if we already have an active connection for this URL
    if (activeConnections.has(formattedUrl)) {
      const existingWs = activeConnections.get(formattedUrl);
      if (existingWs && existingWs.readyState !== WebSocket.CLOSED) {
        console.log(`[WEBSOCKET] Reusing existing connection for ${formattedUrl}`);
        webSocketRef.current = existingWs;
        
        // Update connection status based on the existing connection
        if (existingWs.readyState === WebSocket.OPEN) {
          setConnectionStatus('Open');
        } else if (existingWs.readyState === WebSocket.CONNECTING) {
          setConnectionStatus('Connecting');
        }
        
        // Increment reference count
        connectionRefCounts.set(formattedUrl, (connectionRefCounts.get(formattedUrl) || 0) + 1);
        
        // Set up event handlers for the existing connection
        setupEventHandlers(existingWs);
        return;
      } else {
        // Remove the closed connection from the registry
        activeConnections.delete(formattedUrl);
        connectionRefCounts.delete(formattedUrl);
      }
    }

    // Create new WebSocket connection
    try {
      console.log(`[WEBSOCKET] Connecting to ${formattedUrl} (attempt ${reconnectCountRef.current + 1}/${reconnectAttempts})`);
      setConnectionStatus('Connecting');
      
      const ws = new WebSocket(formattedUrl);
      webSocketRef.current = ws;
      
      // Register this connection in the global registry
      activeConnections.set(formattedUrl, ws);
      connectionRefCounts.set(formattedUrl, 1);
      
      // Set up event handlers
      setupEventHandlers(ws);
    } catch (error) {
      console.error('[WEBSOCKET] Failed to create WebSocket connection:', error);
      setConnectionStatus('Error');
      
      // Try to reconnect after a delay with exponential backoff
      const reconnectDelay = Math.min(
        reconnectInterval * Math.pow(1.5, reconnectCountRef.current) + (Math.random() * 1000), // Add jitter
        30000 // Cap at 30 seconds
      );
      
      if (reconnectCountRef.current < reconnectAttempts && isComponentMountedRef.current) {
        reconnectTimeoutRef.current = setTimeout(() => {
          reconnectCountRef.current++;
          connect();
        }, reconnectDelay);
      }
    }
  }, [url, onMessage, onOpen, onClose, onError, reconnectAttempts, reconnectInterval, pingInterval, connectionId]);
  
  // Helper function to set up event handlers for a WebSocket
  const setupEventHandlers = useCallback((ws: WebSocket) => {
    if (!ws) return;

    ws.onopen = (event) => {
      if (!isComponentMountedRef.current) return;
      
      console.log('[WEBSOCKET] Connection established');
      setConnectionStatus('Open');
      reconnectCountRef.current = 0; // Reset counter on successful connection
      lastMessageTimeRef.current = Date.now();
      
      // Process any queued messages
      if (messageQueueRef.current.length > 0) {
        console.log(`[WEBSOCKET] Processing ${messageQueueRef.current.length} queued messages`);
        
        // Create a copy and clear the queue before processing
        const queuedMessages = [...messageQueueRef.current];
        messageQueueRef.current = [];
        
        // Send each queued message
        queuedMessages.forEach(message => {
          try {
            ws.send(message);
          } catch (error) {
            console.error('[WEBSOCKET] Error sending queued message:', error);
            // Re-queue the message if sending fails
            messageQueueRef.current.push(message);
          }
        });
      }
      
      // Set up ping interval to keep connection alive
      pingIntervalRef.current = setInterval(() => {
        if (!isComponentMountedRef.current) {
          if (pingIntervalRef.current) {
            clearInterval(pingIntervalRef.current);
            pingIntervalRef.current = null;
          }
          return;
        }
        
        if (ws.readyState === WebSocket.OPEN) {
          try {
            // Send a ping with a timestamp to track round-trip time
            const pingTimestamp = Date.now();
            ws.send(JSON.stringify({ 
              type: 'ping',
              timestamp: pingTimestamp,
              client_info: {
                url: connectionUrlRef.current,
                reconnect_count: reconnectCountRef.current,
                connection_id: connectionId
              }
            }));
          
            console.log('[WEBSOCKET] Ping sent at', new Date(pingTimestamp).toISOString());
          
            // Check if we've received any message recently
            const timeSinceLastMessage = Date.now() - lastMessageTimeRef.current;
            if (timeSinceLastMessage > 30000) { // 30s for faster reconnection
              console.warn('[WEBSOCKET] No messages received for 30 seconds, reconnecting...');
            
              // Close with a specific code to indicate client-initiated reconnect
              ws.close(3001, "Client-initiated reconnect due to inactivity");
            
              // Wait a moment before reconnecting to avoid immediate reconnection
              setTimeout(() => {
                if (isComponentMountedRef.current && !isDisabledRef.current) {
                  connect();
                }
              }, 500);
            }
          } catch (error) {
            console.error('[WEBSOCKET] Error sending ping:', error);
          }
        }
      }, pingInterval);
      
      if (onOpen) onOpen(event);
    };

    ws.onmessage = (event) => {
      if (!isComponentMountedRef.current) return;
      
      try {
        // Log receipt of message for debugging
        console.log('[WEBSOCKET] Message received:', typeof event.data === 'string' ? event.data.substring(0, 50) + '...' : 'Binary data');
        setLastMessage(event);
        lastMessageTimeRef.current = Date.now();
        if (onMessage) onMessage(event);
      } catch (error) {
        console.error('[WEBSOCKET] Error handling message:', error);
      }
    };

    ws.onclose = (event) => {
      console.log(`[WEBSOCKET] Connection closed with code ${event.code}, reason: ${event.reason}`);
      
      // Remove from active connections if this is the registered connection
      if (activeConnections.get(connectionUrlRef.current) === ws) {
        activeConnections.delete(connectionUrlRef.current);
        connectionRefCounts.delete(connectionUrlRef.current);
      }
      
      if (!isComponentMountedRef.current) {
        console.log('[WEBSOCKET] Component unmounted, not updating state or reconnecting');
        return;
      }
      
      setConnectionStatus('Closed');
      
      // Clear ping interval
      if (pingIntervalRef.current) {
        clearInterval(pingIntervalRef.current);
        pingIntervalRef.current = null;
      }
      
      if (onClose) onClose(event);
      
      // Don't attempt to reconnect if disabled or not authenticated (when required)
      if (isDisabledRef.current || (requireAuth && !isAuthenticatedRef.current)) {
        let reason = isDisabledRef.current ? "disabled flag is set" : "user not authenticated";
        console.log(`[WEBSOCKET] Not reconnecting - ${reason}`);
        return;
      }
      
      // Use a more aggressive reconnection strategy with exponential backoff and jitter
      const baseDelay = reconnectInterval;
      const exponentialFactor = 1.3; // Reduced exponential factor
      let delay = baseDelay * Math.pow(exponentialFactor, reconnectCountRef.current);
      
      // Add jitter to prevent thundering herd
      const jitter = Math.random() * 0.2 * delay;
      delay += jitter;
      
      // Cap at reasonable maximum - 10s for faster reconnection
      delay = Math.min(delay, 10000);
      
      console.log(`[WEBSOCKET] Attempting to reconnect in ${Math.round(delay)}ms (attempt ${reconnectCountRef.current + 1}/${reconnectAttempts})`);
      
      if (reconnectCountRef.current < reconnectAttempts) {
        reconnectTimeoutRef.current = setTimeout(() => {
          if (isComponentMountedRef.current && !isDisabledRef.current && 
              (!requireAuth || isAuthenticatedRef.current)) {
            reconnectCountRef.current++;
            connect();
          }
        }, delay);
      } else {
        console.log(`[WEBSOCKET] Max reconnect attempts reached, will continue trying with longer intervals`);
        // Instead of giving up, continue trying with a longer interval
        reconnectTimeoutRef.current = setTimeout(() => {
          if (isComponentMountedRef.current && !isDisabledRef.current && 
              (!requireAuth || isAuthenticatedRef.current)) {
            console.log('[WEBSOCKET] Continuing reconnection attempts after max attempts reached');
            reconnectCountRef.current = Math.max(reconnectCountRef.current - 2, 0); // Reset counter partially
            connect();
          }
        }, 5000);
        setConnectionStatus('Error');
      }
    };

    ws.onerror = (event) => {
      console.error('[WEBSOCKET] Connection error:', event);
      
      // Log additional diagnostic information
      console.error('[WEBSOCKET] Error details:', {
        url: connectionUrlRef.current,
        readyState: ws.readyState,
        protocol: ws.protocol,
        bufferedAmount: ws.bufferedAmount,
        connectionId: connectionId
      });
      
      // Don't set status to Error here - let onclose handle it
      if (onError) onError(event);
    };
  }, [url, onMessage, onOpen, onClose, onError, reconnectAttempts, reconnectInterval, pingInterval]);

  // Initial connection and cleanup
  useEffect(() => {
    // Set component mounted flag
    isComponentMountedRef.current = true;
    
    // Only connect if not disabled and authenticated (when required)
    if (!isDisabledRef.current && (!requireAuth || isAuthenticatedRef.current)) {
      connect();
    } else {
      let reason = isDisabledRef.current ? "disabled flag is set" : "user not authenticated";
      console.log(`[WEBSOCKET] Initial connection prevented - ${reason}`);
      setConnectionStatus('Closed');
    }
    
    // Cleanup on unmount
    return () => {
      // Set component unmounted flag first to prevent any new operations
      isComponentMountedRef.current = false;
      
      console.log('[WEBSOCKET] Component unmounting, cleaning up resources');
      
      // Decrement reference count for this connection
      if (connectionUrlRef.current && connectionRefCounts.has(connectionUrlRef.current)) {
        const currentCount = connectionRefCounts.get(connectionUrlRef.current) || 0;
        if (currentCount <= 1) {
          // Last reference, close and remove the connection
          console.log(`[WEBSOCKET] Last reference to connection ${connectionUrlRef.current}, closing`);
          if (webSocketRef.current) {
            webSocketRef.current.close(1000, "Component unmounted - last reference");
            activeConnections.delete(connectionUrlRef.current);
            connectionRefCounts.delete(connectionUrlRef.current);
          }
        } else {
          // Decrement reference count
          console.log(`[WEBSOCKET] Decreasing reference count for ${connectionUrlRef.current} to ${currentCount - 1}`);
          connectionRefCounts.set(connectionUrlRef.current, currentCount - 1);
        }
      }
      
      // Clear all timers
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
        reconnectTimeoutRef.current = null;
      }
      
      if (pingIntervalRef.current) {
        clearInterval(pingIntervalRef.current);
        pingIntervalRef.current = null;
      }
    };
  }, [connect, requireAuth, isAuthenticated]);

  return {
    lastMessage,
    connectionStatus,
    sendMessage,
    sendCommand,
    reconnect: connect
  };
}

// Specialized hook for workflow executions
export function useWorkflowWebSocket(
  executionId: string, 
  disabled: boolean = false,
  connectionId: string = `workflow-${executionId}-${Date.now()}-${Math.random().toString(36).substring(2, 9)}`
): UseWebSocketReturn {
  // Determine WebSocket URL based on environment
  const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
  const host = window.location.host;
  
  // Ensure executionId is valid before creating the URL
  const validExecutionId = executionId && typeof executionId === 'string' && executionId.trim() !== '';
  
  // Only create a valid WebSocket URL if we have a valid executionId
  const wsUrl = validExecutionId 
    ? `${protocol}//${host}/ws/workflows/${executionId}`
    : '';
  
  const handleMessage = useCallback((event: MessageEvent) => {
    try {
      const message = JSON.parse(event.data);
      console.log('[WORKFLOW_WS] Message received:', message.type);
      
      // Dispatch a custom event that components can listen for
      const customEvent = new CustomEvent('workflow-message', { 
        detail: { message, executionId } 
      });
      window.dispatchEvent(customEvent);
    } catch (error) {
      console.error('[WORKFLOW_WS] Error parsing message:', error);
    }
  }, [executionId]);

  const handleOpen = useCallback((event: Event) => {
    console.log('[WORKFLOW_WS] Connection established for workflow:', executionId);
  }, [executionId]);

  const handleClose = useCallback((event: CloseEvent) => {
    console.log('[WORKFLOW_WS] Connection closed for workflow:', executionId, 'Code:', event.code);
  }, [executionId]);

  const handleError = useCallback((event: Event) => {
    console.error('[WORKFLOW_WS] Connection error for workflow:', executionId, event);
  }, [executionId]);

  // Log the disabled state and URL for debugging
  if (disabled) {
    console.log(`[WORKFLOW_WS] WebSocket for execution ${executionId} is disabled (completed execution)`);
  } else if (validExecutionId) {
    console.log(`[WORKFLOW_WS] Creating WebSocket connection to ${wsUrl} with ID ${connectionId}`);
  } else {
    console.log(`[WORKFLOW_WS] Invalid execution ID: ${executionId}, not creating WebSocket`);
  }

  return useWebSocket({
    url: wsUrl,
    onMessage: handleMessage,
    onOpen: handleOpen,
    onClose: handleClose,
    onError: handleError,
    reconnectAttempts: 5,
    reconnectInterval: 1000,
    pingInterval: 30000,
    disabled: disabled || !validExecutionId,
    connectionId: connectionId
  });
}
